import React from 'react';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import { Skeleton, EmptyState } from './index';

export type Column<T> = {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  sortKey?: string;
  className?: string;
  skeleton?: React.ReactNode;
};

type DataTableProps<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  initialLoading?: boolean;
  sort?: string;
  dir?: 'asc' | 'desc';
  onSort?: (sortKey: string) => void;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  emptyTitle?: string;
  emptyBody?: React.ReactNode;
  skeletonRows?: number;
};

function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  initialLoading,
  sort,
  dir,
  onSort,
  onRowClick,
  selectedKey,
  emptyTitle = 'No results',
  emptyBody,
  skeletonRows = 10,
}: DataTableProps<T>) {
  return (
    <div className="table-scroll">
      <table className={`data-table${loading && !initialLoading ? ' table-loading' : ''}`}>
        <thead>
          <tr>
            {columns.map((column) => {
              const sortable = !!column.sortKey && !!onSort;
              const sorted = sortable && sort === column.sortKey;
              return (
                <th
                  key={column.key}
                  className={`${column.className || ''}${sortable ? ' is-sortable' : ''}${sorted ? ' is-sorted' : ''}`}
                  onClick={sortable ? () => onSort!(column.sortKey!) : undefined}
                  aria-sort={sorted ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {column.header}
                  {sorted &&
                    (dir === 'asc' ? <FiArrowUp className="sort-icon" /> : <FiArrowDown className="sort-icon" />)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {initialLoading
            ? Array.from({ length: skeletonRows }).map((_, index) => (
                <tr key={`s${index}`} aria-hidden="true">
                  {columns.map((column) => (
                    <td key={column.key} className={column.className}>
                      {column.skeleton || <Skeleton width={`${50 + ((index * 7 + column.key.length * 11) % 40)}%`} />}
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => {
                const key = rowKey(row);
                return (
                  <tr
                    key={key}
                    className={selectedKey === key ? 'is-selected' : ''}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (event) => {
                            if (event.key === 'Enter') onRowClick(row);
                          }
                        : undefined
                    }
                  >
                    {columns.map((column) => (
                      <td key={column.key} className={column.className}>
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
        </tbody>
      </table>
      {!initialLoading && !loading && rows.length === 0 && <EmptyState title={emptyTitle}>{emptyBody}</EmptyState>}
    </div>
  );
}

export default DataTable;
